<template>
  <div class="chamados-page">
    <div class="page-container">
      <div class="page-header">
        <h3 class="page-title">{{ t('Chamados') }}</h3>
        <div class="page-actions">
          <AppButton
            :label="t('Novo chamado')"
            icon="pi pi-plus"
            no-explanation="the label says it: opens the new-ticket form"
            data-testid="chamados-btn-novo"
            @click="handleNovo"
          />
        </div>
      </div>

      <div class="page-abas">
        <button
          v-for="opcao in ABAS"
          :key="opcao.valor"
          v-explain.bottom="EX.tabs[opcao.valor]"
          type="button"
          class="aba"
          :class="{ 'aba--ativa': aba === opcao.valor }"
          :data-testid="`chamados-aba-${opcao.valor}`"
          @click="handleTrocarAba(opcao.valor)"
        >
          {{ t(opcao.rotulo) }}
        </button>
      </div>

      <div class="page-body">
        <ChamadosTable
          :chamados="chamados"
          :sou-suporte="souSuporte"
          @abrir="handleAbrir"
          @atualizar="recarregar"
        />
      </div>
    </div>

    <!-- ChamadosModal é centralizado em ModalManager.vue e aberto por eventBus -->
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, provide } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import AppButton from '../../ui/AppButton.vue';
import ChamadosTable from './ChamadosTable.vue';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useChamados, type AbaChamados } from '../../composables/useChamados';
import { ticketExplanations } from './explanations';

/**
 * ChamadosPage — a lista de chamados com as duas abas.
 *
 * As abas são "Em andamento" (padrão) e "Resolvidos", e a terceira ("Todos")
 * existe para quem quer procurar sem pensar em status. A partição vem do
 * backend: `ChamadoStatus::EM_ANDAMENTO` e o COMPLEMENTO dela — nunca duas
 * listas escritas à mão, que divergiriam no dia em que um status novo entrasse.
 *
 * A página abre já em "Em andamento" de propósito: quem entra aqui quer ver o
 * que está pendente, não o arquivo. O histórico continua a um clique — some da
 * vista, não do alcance.
 */

const { t } = useI18n();

const EX = computed(() => ticketExplanations(t));
const route = useRoute();
const { chamados, aba, souSuporte, carregarLista } = useChamados();

const props = defineProps<{
  translations: Record<string, unknown>;
}>();

provide('translations', props.translations);

const ABAS: { valor: AbaChamados; rotulo: string }[] = [
  { valor: 'em_andamento', rotulo: 'Em andamento' },
  { valor: 'encerrados', rotulo: 'Resolvidos' },
  { valor: 'todos', rotulo: 'Todos' },
];

const recarregar = async (): Promise<void> => {
  try {
    await carregarLista();
  } catch (e) {
    const detalhe = e instanceof Error ? e.message : String(e);
    eventBus.emit('globalMessage.error', t('Erro ao carregar os chamados') + ': ' + detalhe);
  }
};

const handleTrocarAba = async (nova: AbaChamados): Promise<void> => {
  log('ChamadosPage', 'handleTrocarAba', 'Aba', nova);

  try {
    await carregarLista(nova);
  } catch (e) {
    const detalhe = e instanceof Error ? e.message : String(e);
    eventBus.emit('globalMessage.error', t('Erro ao carregar os chamados') + ': ' + detalhe);
  }
};

const handleNovo = (): void => {
  eventBus.emit('modal.chamados.open', { modo: 'novo' });
};

const handleAbrir = (id: number): void => {
  eventBus.emit('modal.chamados.open', { modo: 'detalhe', id });
};

/**
 * Lê o `?id=` da URL — o destino do botão do e-mail de aviso e da rota gravada
 * em cada notificação do sino (`/chamados?id=N`, ver Chamados::corpoDoAviso()).
 *
 * Devolve `null` para qualquer coisa que não seja um id plausível: sem isso,
 * `/chamados?id=abc` viraria `NaN` e pediria o detalhe de um chamado
 * inexistente só para o modal abrir e fechar com erro na cara de quem chegou
 * de um link.
 */
const idDaUrl = (): number | null => {
  const bruto = route.query.id;
  const texto = Array.isArray(bruto) ? bruto[0] : bruto;

  if (typeof texto !== 'string' || texto.trim() === '') return null;

  const numero = Number(texto);

  return Number.isInteger(numero) && numero > 0 ? numero : null;
};

onMounted(() => {
  eventBus.on('chamados.refresh', recarregar);
  void recarregar();

  const id = idDaUrl();

  if (id === null) return;

  /*
   * `nextTick` NÃO É ENFEITE: o ChamadosModal só passa a escutar
   * `modal.chamados.open` no onMounted DELE, e ele mora no ModalManager, que em
   * App.vue vem DEPOIS do <router-view/> — ou seja, monta depois desta página.
   * Emitir aqui de forma síncrona joga o evento no vazio: ninguém escutando,
   * nenhum erro, e a pessoa que clicou no link do e-mail cai na lista tendo que
   * caçar o próprio chamado — exatamente o defeito que isto conserta.
   *
   * O `?id=` FICA na URL de propósito: é um link, e um link tem de continuar
   * valendo se a pessoa recarregar ou repassar a alguém.
   */
  void nextTick(() => {
    log('ChamadosPage', 'onMounted', 'Abrindo o chamado vindo da URL', id);
    eventBus.emit('modal.chamados.open', { modo: 'detalhe', id });
  });
});

onBeforeUnmount(() => {
  eventBus.off('chamados.refresh', recarregar);
});
</script>

<style scoped>
.chamados-page {
  padding: 0;
}

.page-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
}

.page-header {
  padding: 15px 20px;
  border-bottom: 1px solid #f4f4f4;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.page-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.page-actions {
  display: flex;
  gap: 10px;
}

.page-abas {
  display: flex;
  gap: 4px;
  padding: 0 20px;
  border-bottom: 1px solid #f4f4f4;
}

.aba {
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  padding: 10px 16px;
  font-size: 0.92rem;
  color: #666;
  cursor: pointer;
}

.aba:hover {
  color: #333;
}

.aba--ativa {
  color: #1565c0;
  border-bottom-color: #1565c0;
  font-weight: 600;
}

.aba:focus-visible {
  outline: 2px solid #1565c0;
  outline-offset: -2px;
}

.page-body {
  padding: 20px;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .page-actions {
    width: 100%;
  }

  .page-actions :deep(.p-button) {
    width: 100%;
  }

  .page-abas {
    overflow-x: auto;
  }
}
</style>
